import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import FileUploadModal from './FileUploadModal';
import FieldMappingTable from './FieldMappingTable';
import RelatedFieldMapping from './RelatedFieldMapping';
import ValidationResultsPanel from './ValidationResultsPanel';
import * as analyticsApi from '../../api/analytics';

/**
 * InsertDataFlow - Multi-step flow for INSERT operations
 *
 * Steps:
 * 1. Upload file (optional - can skip if file already uploaded)
 * 2. Preview data
 * 3. Map fields
 * 4. Configure lookups (optional)
 * 5. Validate
 * 6. Confirm and submit
 * 7. Progress indicator during insert
 * 8. Results page (success count, failure count, download links)
 */
function InsertDataFlow({ initialFileId, initialObjectName, initialDataSourceId, onClose }) {
  const { activeTenant } = useSelector((state) => state.auth);
  const { selectedDataSource } = useSelector((state) => state.dataSources);

  // Step state
  const [currentStep, setCurrentStep] = useState(initialFileId ? 2 : 1); // Skip upload if file provided
  const [fileId, setFileId] = useState(initialFileId || null);
  const [objectName, setObjectName] = useState(initialObjectName || '');
  const [dataSourceId, setDataSourceId] = useState(initialDataSourceId || selectedDataSource?.data_source_id || '');

  // Data state
  const [filePreview, setFilePreview] = useState(null);
  const [availableObjects, setAvailableObjects] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [lookupMappings, setLookupMappings] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [workflowId, setWorkflowId] = useState(null);
  const [operationStatus, setOperationStatus] = useState(null);

  // API configuration state
  const [apiType, setApiType] = useState('bulk'); // 'bulk' or 'batch'
  const [processingMode, setProcessingMode] = useState('parallel'); // 'parallel' or 'serial'
  const [batchSize, setBatchSize] = useState(10000); // Batch size for bulk API

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [validating, setValidating] = useState(false);

  // Load data sources on mount and whenever selectedDataSource changes
  useEffect(() => {
    loadDataSources();
  }, [selectedDataSource]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load file preview when fileId changes
  useEffect(() => {
    if (fileId && currentStep >= 2) {
      loadFilePreview();
    }
  }, [fileId, currentStep]);

  // Poll operation status when workflow is running
  useEffect(() => {
    if (workflowId && currentStep === 7) {
      const MAX_RETRIES = 150; // ~5 minutes at 2s intervals
      let retryCount = 0;

      const interval = setInterval(async () => {
        retryCount++;
        if (retryCount > MAX_RETRIES) {
          clearInterval(interval);
          setError('Operation status polling timed out after 5 minutes. The operation may still be running — check back later.');
          setCurrentStep(8);
          return;
        }

        try {
          const status = await analyticsApi.getOperationStatus(workflowId);
          setOperationStatus(status);

          if (status.status === 'completed' || status.status === 'failed') {
            clearInterval(interval);
            setCurrentStep(8); // Move to results page
          }
        } catch (err) {
          console.error('Failed to poll operation status:', err);
          setError(err.response?.data?.message || err.message || 'Failed to poll operation status');
        }
      }, 2000); // Poll every 2 seconds

      setPollingInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [workflowId, currentStep]);

  const loadDataSources = async () => {
    try {
      // Use the selected data source from Redux store
      if (selectedDataSource) {
        setDataSources([{
          id: selectedDataSource.data_source_id,
          name: selectedDataSource.name,
          type: selectedDataSource.source_type,
        }]);
        if (!dataSourceId) {
          setDataSourceId(selectedDataSource.data_source_id);
        }
      }
    } catch (err) {
      console.error('Failed to load data sources:', err);
    }
  };

  const loadFilePreview = async () => {
    setLoading(true);
    setError(null);

    try {
      const preview = await analyticsApi.getFilePreview(fileId);
      setFilePreview(preview);
    } catch (err) {
      console.error('Failed to load file preview:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load file preview');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUploadComplete = (response) => {
    setFileId(response.file_id);
    setShowUploadModal(false);
    setCurrentStep(2); // Move to preview step
  };

  const handleObjectSelect = async (selectedObject) => {
    setObjectName(selectedObject);
    setCurrentStep(3); // Move to mapping step
  };

  const handleMappingsChange = (newMappings) => {
    setMappings(newMappings);
  };

  const handleLookupMappingsChange = (newLookupMappings) => {
    setLookupMappings(newLookupMappings);
  };

  const handleValidate = async () => {
    setValidating(true);
    setError(null);

    try {
      // Verify we have required data
      if (!fileId || !objectName || !dataSourceId) {
        throw new Error('Missing required fields for validation');
      }

      // Run validation
      const result = await analyticsApi.validateData(fileId, objectName, dataSourceId);
      setValidationResult(result);

      // Auto-advance if validation passed
      if (result.valid) {
        setTimeout(() => {
          setCurrentStep(5); // Move to confirm step
        }, 1000);
      }
    } catch (err) {
      console.error('Validation failed:', err);
      setError(err.response?.data?.message || err.message || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmitInsert = async () => {
    setLoading(true);
    setError(null);

    try {
      // Verify we have required data
      if (!fileId || !objectName || !dataSourceId) {
        throw new Error('Missing required fields');
      }

      // Prepare API configuration
      const apiConfig = {
        apiType,
        processingMode,
        batchSize: apiType === 'bulk' ? batchSize : 200, // Batch API max is 200
      };

      // Start the INSERT workflow
      const response = await analyticsApi.insertRecords(fileId, objectName, dataSourceId, apiConfig);
      setWorkflowId(response.workflow_id);
      setCurrentStep(7); // Move to progress step
    } catch (err) {
      console.error('Failed to start insert operation:', err);
      setError(err.response?.data?.message || err.message || 'Failed to start insert operation');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setCurrentStep(1);
    setFileId(null);
    setObjectName('');
    setMappings([]);
    setWorkflowId(null);
    setOperationStatus(null);
    setError(null);
  };

  const handleDownloadResults = async (type) => {
    if (!operationStatus?.run_id) {
      setError('No results available for download');
      return;
    }

    try {
      await analyticsApi.downloadOperationResults(operationStatus.run_id, type);
    } catch (err) {
      console.error('Failed to download results:', err);
      setError(err.message || 'Failed to download results');
    }
  };

  // Step 1: Upload file
  const renderUploadStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Step 1: Upload File</h3>
        <p className="text-sm text-gray-500 mt-1">
          Upload a CSV or Excel file containing the records you want to insert
        </p>
      </div>

      <div className="text-center py-12">
        <button
          onClick={() => setShowUploadModal(true)}
          className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-medium text-lg"
        >
          Upload File
        </button>
      </div>

      {showUploadModal && (
        <FileUploadModal
          onClose={() => setShowUploadModal(false)}
          onUploadComplete={handleFileUploadComplete}
        />
      )}
    </div>
  );

  // Step 2: Preview data and select object
  const renderPreviewStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Step 2: Preview Data & Select Object</h3>
        <p className="text-sm text-gray-500 mt-1">
          Review your data and choose which object to insert into
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="ml-3 text-gray-600">Loading preview...</p>
        </div>
      ) : filePreview ? (
        <>
          {/* Object selection */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Object
            </label>
            <select
              value={objectName}
              onChange={(e) => setObjectName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">-- Select object --</option>
              <option value="Account">Account</option>
              <option value="Contact">Contact</option>
              <option value="Opportunity">Opportunity</option>
              <option value="Lead">Lead</option>
              <option value="Case">Case</option>
            </select>
          </div>

          {/* Preview table */}
          <div>
            <h4 className="font-medium text-gray-900 mb-2">
              Preview (first {filePreview.sample_rows?.length || 0} of {filePreview.total_rows} rows)
            </h4>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-80">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {filePreview.column_headers?.map((header, idx) => (
                        <th
                          key={idx}
                          className="px-3 py-2 text-left font-medium text-gray-900 whitespace-nowrap border-b border-gray-200"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filePreview.sample_rows?.map((row, rowIdx) => (
                      <tr key={rowIdx} className="hover:bg-gray-50">
                        {filePreview.column_headers?.map((header, cellIdx) => (
                          <td
                            key={cellIdx}
                            className="px-3 py-2 text-gray-700 whitespace-nowrap"
                          >
                            {row[header] || <span className="text-gray-400 italic">empty</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Next button */}
          <div className="flex justify-end">
            <button
              onClick={() => handleObjectSelect(objectName)}
              disabled={!objectName}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Map Fields
            </button>
          </div>
        </>
      ) : null}
    </div>
  );

  // Step 3: Map fields
  const renderMappingStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Step 3: Map Fields</h3>
        <p className="text-sm text-gray-500 mt-1">
          Map your file columns to {objectName} fields
        </p>
      </div>

      <FieldMappingTable
        fileId={fileId}
        objectName={objectName}
        sourceType={selectedDataSource?.source_type}
        dataSourceId={dataSourceId}
        onMappingsChange={handleMappingsChange}
      />

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep(2)}
          className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition font-medium"
        >
          Back
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => setCurrentStep(5)} // Skip lookups, go to validation
            disabled={mappings.filter(m => m.target_field || m.static_value).length === 0}
            className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Skip Lookups
          </button>
          <button
            onClick={() => setCurrentStep(4)} // Move to lookup step
            disabled={mappings.filter(m => m.target_field || m.static_value).length === 0}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next: Configure Lookups
          </button>
        </div>
      </div>
    </div>
  );

  // Step 4: Configure lookup mappings
  const renderLookupStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Step 4: Configure Lookups (Optional)</h3>
        <p className="text-sm text-gray-500 mt-1">
          Configure how lookup/relationship fields should be resolved from file values
        </p>
      </div>

      <RelatedFieldMapping
        fileId={fileId}
        objectName={objectName}
        sourceType={selectedDataSource?.source_type}
        dataSourceId={dataSourceId}
        lookupMappings={lookupMappings}
        onLookupMappingsChange={handleLookupMappingsChange}
      />

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep(3)}
          className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition font-medium"
        >
          Back
        </button>
        <button
          onClick={() => setCurrentStep(5)} // Move to validation step
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium"
        >
          Next: Validate Data
        </button>
      </div>
    </div>
  );

  // Step 5: Validate data
  const renderValidationStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Step 5: Validate Data</h3>
        <p className="text-sm text-gray-500 mt-1">
          Check your data for errors before inserting into {objectName}
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {!validationResult && !validating && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-medium text-blue-900 mb-2">Pre-validation recommended</p>
              <p className="text-sm text-blue-800">
                Validating your data before insert helps catch errors early:
              </p>
              <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
                <li>Required fields are not empty</li>
                <li>Field types match (numbers, dates, etc.)</li>
                <li>Picklist values are valid</li>
                <li>Field length constraints are met</li>
                <li>Date and email formats are correct</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {validating && (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="ml-4 text-gray-600">Validating your data...</p>
        </div>
      )}

      {validationResult && (
        <ValidationResultsPanel
          validationResult={validationResult}
          allowProceed={validationResult.valid}
        />
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep(4)}
          disabled={validating}
          className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition font-medium disabled:opacity-50"
        >
          Back
        </button>
        <div className="flex gap-3">
          {!validationResult && (
            <button
              onClick={handleValidate}
              disabled={validating}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {validating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Validating...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Run Validation
                </>
              )}
            </button>
          )}
          {validationResult && validationResult.valid && (
            <button
              onClick={() => setCurrentStep(6)}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium"
            >
              Next: Review & Submit
            </button>
          )}
          {validationResult && !validationResult.valid && (
            <button
              onClick={() => setCurrentStep(3)}
              className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition font-medium"
            >
              Go Back and Fix Mappings
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Step 6: Confirm and submit
  const renderConfirmStep = () => {
    const mappedFields = mappings.filter(m => m.target_field || m.static_value);
    const autoMappedCount = mappedFields.filter(m => m.is_auto_mapped).length;
    const manualMappedCount = mappedFields.length - autoMappedCount;

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Step 5: Review & Submit</h3>
          <p className="text-sm text-gray-500 mt-1">
            Review your insert operation details before submitting
          </p>
        </div>

        {/* Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="font-medium text-blue-900 mb-4">Operation Summary</h4>
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-sm text-blue-700">File</dt>
              <dd className="text-base font-medium text-blue-900">{filePreview?.file_id}</dd>
            </div>
            <div>
              <dt className="text-sm text-blue-700">Target Object</dt>
              <dd className="text-base font-medium text-blue-900">{objectName}</dd>
            </div>
            <div>
              <dt className="text-sm text-blue-700">Total Rows</dt>
              <dd className="text-base font-medium text-blue-900">{filePreview?.total_rows}</dd>
            </div>
            <div>
              <dt className="text-sm text-blue-700">Mapped Fields</dt>
              <dd className="text-base font-medium text-blue-900">
                {mappedFields.length} ({autoMappedCount} auto, {manualMappedCount} manual)
              </dd>
            </div>
          </dl>
        </div>

        {/* API Configuration */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="font-medium text-gray-900 mb-4">API Configuration</h4>
          <div className="space-y-4">
            {/* API Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Type
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="bulk"
                    checked={apiType === 'bulk'}
                    onChange={(e) => setApiType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Bulk API</span>
                    <span className="text-gray-500 ml-2">(Better for large datasets)</span>
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="batch"
                    checked={apiType === 'batch'}
                    onChange={(e) => setApiType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Batch API</span>
                    <span className="text-gray-500 ml-2">(Triggers automation, max 200 records)</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Processing Mode */}
            {apiType === 'bulk' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Processing Mode
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="parallel"
                      checked={processingMode === 'parallel'}
                      onChange={(e) => setProcessingMode(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Parallel</span>
                      <span className="text-gray-500 ml-2">(Faster)</span>
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="serial"
                      checked={processingMode === 'serial'}
                      onChange={(e) => setProcessingMode(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Serial</span>
                      <span className="text-gray-500 ml-2">(Preserves order)</span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Batch Size */}
            {apiType === 'bulk' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Batch Size
                </label>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={batchSize}
                  onChange={(e) => setBatchSize(parseInt(e.target.value) || 10000)}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Records per batch (1-10,000)
                </p>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">Before you proceed</p>
              <p className="text-sm text-yellow-700 mt-1">
                This operation will create {filePreview?.total_rows} new {objectName} records in Salesforce.
                Make sure your data is correct before submitting.
              </p>
            </div>
          </div>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between">
          <button
            onClick={() => setCurrentStep(5)}
            disabled={loading}
            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition font-medium disabled:opacity-50"
          >
            Back
          </button>
          <button
            onClick={handleSubmitInsert}
            disabled={loading}
            className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Starting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Submit Insert Operation
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Step 7: Progress indicator
  const renderProgressStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Inserting Records...</h3>
        <p className="text-sm text-gray-500 mt-1">
          Please wait while we insert your records into Salesforce
        </p>
      </div>

      <div className="bg-blue-50 rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto mb-4"></div>
        <p className="font-medium text-gray-900 mb-2">Operation in progress</p>
        <p className="text-sm text-gray-600">
          {operationStatus?.message || 'Inserting records into Salesforce...'}
        </p>
      </div>
    </div>
  );

  // Step 8: Results page
  const renderResultsStep = () => {
    const isSuccess = operationStatus?.success;
    const succeededCount = operationStatus?.records_succeeded || 0;
    const failedCount = operationStatus?.records_failed || 0;
    const totalCount = operationStatus?.records_processed || 0;

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Results</h3>
          <p className="text-sm text-gray-500 mt-1">
            Your insert operation has completed
          </p>
        </div>

        {/* Success/Failure indicator */}
        <div className={`rounded-lg p-6 ${isSuccess ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isSuccess ? 'bg-green-100' : 'bg-red-100'}`}>
              {isSuccess ? (
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <h4 className={`text-lg font-medium ${isSuccess ? 'text-green-900' : 'text-red-900'}`}>
                {isSuccess ? 'Insert Completed Successfully' : 'Insert Failed'}
              </h4>
              <p className={`text-sm mt-1 ${isSuccess ? 'text-green-700' : 'text-red-700'}`}>
                {operationStatus?.error || `Processed ${totalCount} records`}
              </p>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-500">Total Processed</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{totalCount}</p>
          </div>
          <div className="bg-white border border-green-200 rounded-lg p-4 text-center">
            <p className="text-sm text-green-600">Succeeded</p>
            <p className="text-3xl font-bold text-green-600 mt-2">{succeededCount}</p>
          </div>
          <div className="bg-white border border-red-200 rounded-lg p-4 text-center">
            <p className="text-sm text-red-600">Failed</p>
            <p className="text-3xl font-bold text-red-600 mt-2">{failedCount}</p>
          </div>
        </div>

        {/* Download Results */}
        {operationStatus?.run_id && (totalCount > 0) && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h4 className="font-medium text-gray-900 mb-3">Download Results</h4>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleDownloadResults('all')}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium text-sm flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download All Results
              </button>
              {succeededCount > 0 && (
                <button
                  onClick={() => handleDownloadResults('successes')}
                  className="px-4 py-2 bg-green-50 border border-green-200 text-green-700 rounded-lg hover:bg-green-100 transition font-medium text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Successes ({succeededCount})
                </button>
              )}
              {failedCount > 0 && (
                <button
                  onClick={() => handleDownloadResults('failures')}
                  className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg hover:bg-red-100 transition font-medium text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Failures ({failedCount})
                </button>
              )}
            </div>
            {failedCount > 0 && (
              <p className="text-sm text-gray-600 mt-3">
                💡 Download the failures CSV to see detailed error messages and fix your data before retrying
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex justify-between">
          <button
            onClick={handleRetry}
            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition font-medium"
          >
            Start New Insert
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium"
          >
            Done
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" role="dialog" aria-modal="true" aria-labelledby="insert-records-modal-title">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 id="insert-records-modal-title" className="text-xl font-bold text-gray-900">Insert Records</h2>
            <p className="text-sm text-gray-500 mt-1">
              Step {currentStep} of 8
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / 8) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto flex-1">
          {currentStep === 1 && renderUploadStep()}
          {currentStep === 2 && renderPreviewStep()}
          {currentStep === 3 && renderMappingStep()}
          {currentStep === 4 && renderLookupStep()}
          {currentStep === 5 && renderValidationStep()}
          {currentStep === 6 && renderConfirmStep()}
          {currentStep === 7 && renderProgressStep()}
          {currentStep === 8 && renderResultsStep()}
        </div>
      </div>
    </div>
  );
}

export default InsertDataFlow;
